<template>
  <OareContentView :title="title" :loading="loading">
    <template #header>
      <router-link :to="backLink"
        >&larr; Back to {{ itemType.toLowerCase() }} view
      </router-link>
    </template>
    <text-and-collections-dialog
      :key="dialogUuid"
      :uuid="dialogUuid"
      :name="dialogName"
      :itemType="itemType"
      v-model="textsAndCollectionsDialog"
    />
    <v-container>
      <v-row class="align-center justify-center">
        <OareDialog
          v-model="addItemsDialog"
          :title="`Add ${itemType}s`"
          submitText="Yes, add"
          cancelText="No, don't add"
          @submit="addListItems"
          :submitLoading="addItemsLoading"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              color="info"
              :disabled="selectedItems.length === 0"
              class="test-add"
              >Add selected {{ itemType }}s ({{ selectedItems.length }})</v-btn
            >
          </template>
          Are you sure you want to add the following
          {{ itemType.toLowerCase() }}(s) to {{ groupName }}?
          <v-data-table :headers="itemsHeaders" :items="selectedItems">
            <template #[`item.imgUrl`]="{ item }">
              <v-img
                :src="item.imgUrl"
                max-height="150"
                max-width="250"
              ></v-img>
            </template>
          </v-data-table>
        </OareDialog>
        <v-spacer />
        <v-col cols="4">
          <v-text-field
            v-model="search"
            label="Search"
            single-line
            hide-details
            clearable
            class="test-search"
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col cols="4">
          <h3>Selected {{ itemType }}s</h3>
          <v-data-table
            :headers="itemsHeaders"
            :items="selectedItems"
            item-value="uuid"
            return-object
            class="mt-3 test-selected-items"
            show-select
            select-strategy="all"
            :model-value="selectedItems"
            @update:model-value="updateSelectedList"
            :loading="selectedListLoading"
            v-model:page="selectedListPage"
          >
            <template #[`item.name`]="{ item }">
              <a
                v-if="item.hasEpigraphy || itemType === 'Collection'"
                @click="setupDialog(item.uuid, item.name || '')"
                class="text-decoration-underline"
                >{{ item.name }}</a
              >
              <span v-else>{{ item.name }}</span>
            </template>
            <template #[`item.imgUrl`]="{ item }">
              <v-img
                :src="item.imgUrl"
                max-height="100"
                max-width="150"
              ></v-img>
            </template>
            <template #no-data>
              No {{ itemType.toLowerCase() }}s selected
            </template>
          </v-data-table>
        </v-col>
        <v-col cols="8">
          <h3>All {{ itemType }}s</h3>
          <select-all-message
            v-if="selectAllMessage"
            :itemType="itemType"
            :serverCount="serverCount"
            :rows="rows"
            @select-full-list="selectFullList"
          />
          <v-data-table-server
            :loading="getItemsLoading"
            :headers="itemsHeaders"
            :items="unaddedItems"
            :items-length="serverCount"
            item-value="uuid"
            return-object
            class="mt-3 test-all-items"
            show-select
            :model-value="selectedItems"
            @update:model-value="updateSelection"
            :page="Number(page)"
            @update:page="page = String($event)"
            :items-per-page="Number(rows)"
            @update:items-per-page="rows = String($event)"
            :items-per-page-options="[10, 25, 50, 100]"
          >
            <template #[`item.name`]="{ item }">
              <a
                v-if="item.hasEpigraphy || itemType === 'Collection'"
                @click="setupDialog(item.uuid, item.name || '')"
                class="text-decoration-underline"
                >{{ item.name }}</a
              >
              <span v-else>{{ item.name }}</span>
            </template>
            <template #[`item.imgUrl`]="{ item }">
              <v-img
                :src="item.imgUrl"
                max-height="150"
                max-width="250"
              ></v-img>
            </template>
          </v-data-table-server>
        </v-col>
      </v-row>
    </v-container>
  </OareContentView>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  Ref,
  onMounted,
  watch,
  computed,
  onBeforeMount,
  onBeforeUnmount,
} from 'vue';
import sl from '@/serviceLocator';
import TextAndCollectionsDialog from './TextAndCollectionsDialog.vue';
import {
  PermissionsListType,
  Pagination,
  DenylistAllowlistItem,
  DenylistAllowlistPayload,
} from '@oare/types';
import { DataTableHeader } from 'vuetify';
import useQueryParam from '@/hooks/useQueryParam';
import SelectAllMessage from './SelectAllMessage.vue';

// Image search results carry an image URL as well as the common fields.
type ListItem = DenylistAllowlistItem & { imgUrl?: string };

export default defineComponent({
  name: 'AddPermissionsItems',
  components: {
    TextAndCollectionsDialog,
    SelectAllMessage,
  },
  props: {
    groupId: {
      type: String,
      required: false,
    },
    itemType: {
      type: String as PropType<PermissionsListType>,
      required: true,
    },
    // The public denylist adder ignores the group id argument.
    addItems: {
      type: Function as PropType<
        (payload: DenylistAllowlistPayload, groupId: number) => Promise<unknown>
      >,
      required: true,
    },
    addingEditPermissions: {
      type: Boolean,
      default: false,
    },
  },
  setup({ groupId, itemType, addItems, addingEditPermissions }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const router = sl.get('router');
    const _ = sl.get('lodash');

    const itemsHeaders: Ref<DataTableHeader[]> = ref(
      itemType === 'Image'
        ? [
            { title: 'Name', key: 'name' },
            { title: 'Image', key: 'imgUrl' },
          ]
        : [{ title: 'Name', key: 'name' }]
    );

    const loading = ref(true);
    const addItemsDialog = ref(false);
    const addItemsLoading = ref(false);
    const getItemsLoading = ref(false);
    const selectedListLoading = ref(false);
    const selectAllMessage = ref(false);
    const textsAndCollectionsDialog = ref(false);
    const dialogUuid = ref('');
    const dialogName = ref('');
    const selectedListPage = ref(1);

    const page = useQueryParam('page', '1', false);
    const rows = useQueryParam('rows', '10', true);
    const search = useQueryParam('query', '', true);
    const savedQueries: Ref<Required<Pagination>> = ref({
      page: 1,
      limit: 10,
      filter: '',
    });

    const serverCount: Ref<number> = ref(0);

    const selectedItems: Ref<ListItem[]> = ref([]);
    const unaddedItems: Ref<ListItem[]> = ref([]);

    const groupName = ref('');

    const backLink = computed(() => {
      if (groupId) {
        return addingEditPermissions
          ? `/groups/${groupId}/edit?type=${itemType.toLowerCase()}`
          : `/groups/${groupId}/allowlist?type=${itemType.toLowerCase()}`;
      }
      return `/admin/denylist/${itemType.toLowerCase()}s`;
    });

    const title = computed(() => {
      if (groupId) {
        return `Add ${itemType}s to ${groupName.value} ${
          addingEditPermissions ? 'Edit Permissions' : 'Allowlist'
        }`;
      } else {
        return `Add ${itemType}s to ${groupName.value}`;
      }
    });

    const getItems = async () => {
      try {
        getItemsLoading.value = true;
        const response = await server.searchNames({
          page: Number(page.value),
          limit: Number(rows.value),
          filter: search.value,
          groupId,
          type: itemType,
          showExcluded: addingEditPermissions,
        });
        unaddedItems.value = response.items;
        serverCount.value = response.count;
      } catch (err) {
        actions.showErrorSnackbar(
          `Error updating ${itemType.toLowerCase()} list. Please try again.`,
          err as Error
        );
      } finally {
        getItemsLoading.value = false;
      }
    };

    const addListItems = async () => {
      const uuids = selectedItems.value.map(item => item.uuid);
      let type: 'img' | 'text' | 'collection';
      switch (itemType) {
        case 'Image':
          type = 'img';
          break;
        case 'Collection':
          type = 'collection';
          break;
        case 'Text':
          type = 'text';
          break;
      }

      const payload: DenylistAllowlistPayload = {
        uuids,
        type: type,
      };
      addItemsLoading.value = true;
      try {
        await addItems(payload, Number(groupId));
        actions.showSnackbar(
          `Successfully added ${itemType.toLowerCase()}(s).`
        );
        // Wait for the saved flag before leaving so the route guard sees it.
        await router.replace({ query: { saved: 'true' } });
        await router.push(backLink.value);
      } catch (err) {
        actions.showErrorSnackbar(
          `Error adding ${itemType.toLowerCase()}(s). Please try again.`,
          err as Error
        );
      } finally {
        addItemsLoading.value = false;
        addItemsDialog.value = false;
      }
    };

    onMounted(async () => {
      try {
        await getItems();
        groupName.value = groupId
          ? (await server.getGroupInfo(Number(groupId))).name
          : 'Public Denylist';
      } catch (err) {
        actions.showErrorSnackbar(
          `Error loading ${itemType.toLowerCase()}s. Please try again.`,
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    // The "All" table's selection only ever adds or removes items from the
    // current page, so diff it against what was already selected.
    const updateSelection = (newSelection: ListItem[]) => {
      const previousUuids = selectedItems.value.map(item => item.uuid);
      const newUuids = newSelection.map(item => item.uuid);
      const added = newSelection.filter(
        item => !previousUuids.includes(item.uuid)
      );
      const kept = selectedItems.value.filter(item =>
        newUuids.includes(item.uuid)
      );

      if (kept.length < selectedItems.value.length) {
        selectAllMessage.value = false;
      } else if (
        added.length === unaddedItems.value.length &&
        Number(serverCount.value) > Number(rows.value)
      ) {
        selectAllMessage.value = true;
      }
      selectedItems.value = [...added, ...kept];
    };

    // Unchecking rows in the "Selected" table drops them from the selection.
    const updateSelectedList = (newSelection: ListItem[]) => {
      selectedItems.value = newSelection;
      if (newSelection.length === 0) {
        selectAllMessage.value = false;
        selectedListPage.value = 1;
      }
    };

    async function selectFullList() {
      selectedListLoading.value = true;
      const response = await server.searchNames({
        page: Number(page.value),
        limit: serverCount.value,
        filter: search.value,
        groupId,
        type: itemType,
        showExcluded: addingEditPermissions,
      });
      const selectedItemsUuids = selectedItems.value.map(item => item.uuid);
      // Keep the rows exactly as searchNames returns them: the "All" table
      // matches selected rows by value, so reshaped copies never show as
      // checked.
      const itemsToAdd = response.items.filter(
        item => !selectedItemsUuids.includes(item.uuid)
      );
      itemsToAdd.forEach(item => selectedItems.value.push(item));
      selectedListLoading.value = false;
      selectAllMessage.value = false;
      actions.showSnackbar(
        `Successfully selected all specified ${itemType.toLowerCase()}s`
      );
    }

    const setupDialog = (uuid: string, name: string) => {
      dialogUuid.value = uuid;
      dialogName.value = name;
      textsAndCollectionsDialog.value = true;
    };

    watch([page, rows], async () => {
      await getItems();
      selectAllMessage.value = false;
    });

    // A search restarts from page 1. If we are already there the page watcher
    // will not fire, so fetch directly.
    watch(
      search,
      _.debounce(async () => {
        selectAllMessage.value = false;
        if (!search.value) {
          search.value = '';
        }
        if (page.value === '1') {
          await getItems();
        } else {
          page.value = '1';
        }
      }, 500)
    );

    watch(textsAndCollectionsDialog, () => {
      if (textsAndCollectionsDialog.value) {
        savedQueries.value = {
          page: Number(page.value),
          limit: Number(rows.value),
          filter: search.value,
        };
        page.value = '1';
        rows.value = '10';
        search.value = '';
      } else {
        page.value = String(savedQueries.value.page);
        rows.value = String(savedQueries.value.limit);
        search.value = savedQueries.value.filter;
      }
    });

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (selectedItems.value.length > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    onBeforeMount(() => {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      selectedItems.value = [];
    });

    return {
      groupName,
      loading,
      addItemsLoading,
      addItemsDialog,
      addListItems,
      selectedItems,
      unaddedItems,
      itemsHeaders,
      search,
      getItemsLoading,
      page,
      rows,
      serverCount,
      updateSelection,
      updateSelectedList,
      selectAllMessage,
      selectFullList,
      selectedListLoading,
      textsAndCollectionsDialog,
      dialogUuid,
      dialogName,
      setupDialog,
      selectedListPage,
      backLink,
      title,
    };
  },
});
</script>
